import { OPTIONALLY_ASSIGNED_SEGMENT_KINDS, OPTIONALLY_ASSIGNED_SEGMENT_KINDS_LABELS } from '@/components/Packaging/constants';
import { isAssignedSegment, isUnassignedOverlay } from '@/components/Packaging/typeGuards';
import { useAppSelector } from '@/redux/hooks';
import { selectCanWriteContentAssemblyTasks } from '@/redux/selectors/authSelectors';
import { OverlayLayer, SegmentItem } from '@/redux/slices/packagingSlice.types';
import { useMemo } from 'react';

import { joinWithAnd } from '@/_utils/utils';

interface Props {
  segments: SegmentItem[];
  overlays: OverlayLayer[];
  isLoadingStatusFulfilled: boolean;
  isSubmitting: boolean;
}

const isSegmentRequiresAssignment = (segment: SegmentItem) => !OPTIONALLY_ASSIGNED_SEGMENT_KINDS.includes(segment.kind);

const checkForUnassignedRequiredSegments = (segments: SegmentItem[]) => {
  return segments.some((segment) => isSegmentRequiresAssignment(segment) && !isAssignedSegment(segment));
};

const getUnassignedOptionalSegments = (segments: SegmentItem[]) => {
  return segments
    .filter((segment) => {
      return !isSegmentRequiresAssignment(segment) && !isAssignedSegment(segment);
    })
    .map((segment) => OPTIONALLY_ASSIGNED_SEGMENT_KINDS_LABELS[segment.kind]);
};

export const useTimelineValidation = ({ segments = [], overlays = [], isLoadingStatusFulfilled, isSubmitting }: Props) => {
  const canWriteContentAssemblyTasks = useAppSelector(selectCanWriteContentAssemblyTasks);

  const isTimelineEmpty = segments.length === 0;
  const hasUnassignedRequiredSegments = useMemo(() => checkForUnassignedRequiredSegments(segments), [segments]);
  const unassignedOptionalSegmentsLabels = useMemo(() => getUnassignedOptionalSegments(segments), [segments]);
  const hasUnassignedOverlays = useMemo(() => overlays.flat().some(isUnassignedOverlay), [overlays]);
  const isCompleteButtonDisabled =
    !canWriteContentAssemblyTasks ||
    !isLoadingStatusFulfilled ||
    isSubmitting ||
    isTimelineEmpty ||
    hasUnassignedRequiredSegments ||
    hasUnassignedOverlays;

  const isSaveDraftButtonDisabled = !canWriteContentAssemblyTasks || !isLoadingStatusFulfilled || isSubmitting;
  const dontHaveWritePermissionMessage = "You don't have permission to change package";
  const completeButtonTooltip = !canWriteContentAssemblyTasks
    ? dontHaveWritePermissionMessage
    : isTimelineEmpty
      ? 'Add at least one segment to the timeline'
      : hasUnassignedRequiredSegments
        ? `Assign all segments. ${joinWithAnd(unassignedOptionalSegmentsLabels)} segments can stay unassigned.`
        : hasUnassignedOverlays
          ? 'Assign inventory to all overlays'
          : undefined;

  const saveDraftButtonTooltip = !canWriteContentAssemblyTasks ? dontHaveWritePermissionMessage : undefined;

  return {
    isCompleteButtonDisabled,
    isSaveDraftButtonDisabled,
    completeButtonTooltip,
    saveDraftButtonTooltip,
    unassignedOptionalSegmentsLabels
  };
};
